import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

/**
 * Public 3-tier pricing rendered from `/api/plans` (single source of truth
 * in `backend/server.py PLANS`). Cards:
 *   Honesty Box · Free for 14 days (try it)
 *   Gate · $20/month (core product — highlighted)
 *   Gate+ · Coming soon (visible, non-clickable)
 *
 * The middle Gate card is the recommended tier and gets the strongest
 * visual weight without using loud "Most Popular" chrome. No comparison
 * matrix, no fake urgency, no aggressive upgrade language.
 */
export default function PricingSection({
  variant = "landing",
  eyebrow = "Pricing",
  heading = "Choose your Gate.",
  subheading = "Start with one product. Move to a full Gate when you're ready. No commission on your sales.",
}) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/plans").then(({ data }) => setData(data)).catch(() => {});
  }, []);
  if (!data) {
    return (
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <p className="text-sm text-stone-500 italic">Loading plans…</p>
      </section>
    );
  }
  const bg = variant === "page" ? "bg-white" : "bg-transparent";
  const CARD_STYLES = {
    honesty_box: "bg-white border-[#DED5C3]",
    gate:        "bg-[#20251F] text-white border-[#20251F]",
    gateplus:    "bg-[#F5F0E5] border-[#DED5C3] opacity-95",
  };
  return (
    <section id="pricing" data-testid="pricing-section" className={`max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 ${bg}`}>
      <p className="font-mono-label text-xs uppercase tracking-widest text-[#3F674E]/80" data-testid="pricing-eyebrow">{eyebrow}</p>
      <h2 className="font-serif-heading text-3xl sm:text-4xl md:text-5xl text-[#20251F] tracking-tight leading-tight mt-2" data-testid="pricing-heading">
        {heading}
      </h2>
      <p className="mt-3 text-sm sm:text-base text-stone-600 max-w-2xl leading-relaxed">
        {subheading}
      </p>

      <div className="mt-8 grid lg:grid-cols-3 gap-5">
        {data.plans.map((p) => {
          const isGate = p.id === "gate";
          const isCS   = p.coming_soon;
          const cardCls = CARD_STYLES[p.id] || "bg-white border-[#DED5C3]";
          const textCol = isGate ? "text-white" : "text-[#20251F]";
          const muted   = isGate ? "text-white/75" : "text-stone-600";
          const featCol = isGate ? "text-white/90" : "text-stone-700";
          const priceCol = isGate ? "text-[#B7D33F]" : "text-[#3F674E]";
          return (
            <div key={p.id} data-testid={`pricing-card-${p.id}`} className={`rounded-3xl border p-6 lg:p-8 flex flex-col ${cardCls}`}>
              <div className="flex items-center gap-2 flex-wrap min-h-[4.25rem] content-center">
                <p className="font-serif-heading text-2xl sm:text-3xl tracking-tight" style={{ color: isGate ? "#ffffff" : "#20251F" }} data-testid={`pricing-label-${p.id}`}>
                  {p.label}
                </p>
                {isCS ? (
                  <span className={`inline-flex items-center whitespace-nowrap rounded-full border ${isGate ? "border-white/25 text-white/80" : "border-[#DED5C3] bg-white text-[#3F674E]"} px-2.5 py-1 font-mono-label text-[10px] uppercase tracking-[0.14em]`} data-testid={`pricing-pill-coming-soon-${p.id}`}>
                    <Clock className="w-3 h-3 inline mr-1" />Coming soon
                  </span>
                ) : p.price_nzd === 0 ? null : (
                  <span className={`font-mono-label text-sm ${priceCol}`}>
                    <span data-testid={`pricing-price-${p.id}`}>${p.price_nzd} NZD</span> / {p.cadence}
                  </span>
                )}
              </div>
              <p className={`mt-2 text-sm min-h-[2.75rem] ${muted}`}>{p.description}</p>
              <ul className={`mt-5 space-y-2 flex-1 ${featCol}`} data-testid={`pricing-features-${p.id}`}>
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm leading-snug">
                    <span className={`shrink-0 mt-0.5 inline-flex w-4 h-4 rounded-full items-center justify-center ${isGate ? "bg-[#B7D33F] text-[#20251F]" : "bg-[#3F674E]/15 text-[#3F674E]"}`}>
                      <Check className="w-3 h-3" strokeWidth={3} />
                    </span>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              {isCS ? (
                <div className="mt-6" data-testid={`pricing-cta-${p.id}`}>
                  <span
                    aria-disabled="true"
                    data-testid={`pricing-pill-preview-${p.id}`}
                    className="w-full inline-flex items-center justify-center h-11 rounded-full border border-[#DED5C3] bg-white text-[#6B6E67] font-mono-label text-[11px] uppercase tracking-[0.14em] px-3 text-center leading-tight"
                  >
                    Preview · not yet available
                  </span>
                </div>
              ) : (
                <Link to={`/seller/signup?plan=${p.id}`} className="mt-6" data-testid={`pricing-cta-${p.id}`}>
                  <Button
                    className={`w-full h-11 rounded-full font-medium px-3 text-[13px] lg:text-sm text-center whitespace-normal leading-tight ${isGate ? "bg-[#B7D33F] hover:bg-[#a3c033] text-[#20251F]" : "bg-[#20251F] hover:bg-black text-white"}`}
                  >
                    {p.cta_label || `Open a ${p.label}`}
                  </Button>
                </Link>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-6 rounded-2xl border border-[#DED5C3] bg-[#FFFCF6] p-5 sm:p-6" data-testid="pricing-footer">
        <p className="font-serif-heading text-lg text-[#20251F]">{data.footer.headline}</p>
        <p className="mt-1.5 text-sm text-stone-700 leading-relaxed">{data.footer.body}</p>
      </div>
    </section>
  );
}
